import { useEffect, useState, useMemo } from 'react';
import { adminExpenseApi, adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Input from '../../components/ui/Input.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { Download, FileSpreadsheet, Archive, Settings2, Loader2, BadgeCheck } from 'lucide-react';
import ExpenseTemplatePage from './ExpenseTemplatePage.jsx';
import ReceiptViewer from '../../components/expense/ReceiptViewer.jsx';
import { buildSingleReportBundle, buildBulkBundle, downloadBlob } from '../../lib/expenseExport.js';

// Compact FX note shown under the SAR total for foreign-currency line
// items — just the rate + the flat 3% fee, highlighted, instead of
// spelling out the full base/fee/total arithmetic. Keyed off the stored
// fee (not currencyType !== 'SAR') so this stays correct regardless of the
// line owner's home currency — the fee is already baked into
// foreignFeeSar by the time it's saved. Falls back to the old SAR check
// for legacy rows saved before foreignFeeSar existed.
function fxNote(item) {
  const isForeign = item.foreignFeeSar != null
    ? item.foreignFeeSar > 0
    : !!item.currencyType && item.currencyType !== 'SAR';
  if (!isForeign) return null;
  const rate = Number(item.exchangeRate || 1);
  return `@ ${rate.toFixed(4)} · +3%`;
}

// This page is the "approved expenses ledger" — it shows only approved
// reports for the export workflow (single XLSX or bulk ZIP). Pending,
// rejected, draft, and cancelled reports live on Expense Requests, where
// admins can also edit / delete / restart-chain them.

export default function AdminExpenseReportsPage() {
  const toast = useToast();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showTemplateConfig, setShowTemplateConfig] = useState(false);
  // Full payload (line items + receipts with viewer URLs) loaded lazily
  // when a row is opened — same pattern as the Requests page.
  const [fullReport, setFullReport] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // Server-side filter so we never even fetch non-approved rows here.
      const res = await adminExpenseApi.listReports({ status: 'approved' });
      setReports(res.reports || []);
    } catch (err) {
      toast.error(`Failed to load reports: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkPaid = async (report) => {
    setMarkingPaidId(report.rowKey);
    try {
      await adminExpenseApi.markPaid(report.partitionKey, report.rowKey);
      toast.success(`${report.claimRef || 'Report'} marked as paid`);
      setSelectedReport(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMarkingPaidId(null);
    }
  };

  useEffect(() => {
    if (!selectedReport) {
      setFullReport(null);
      return;
    }
    setFullReport(null);
    setLoadingFull(true);
    adminExpenseApi.getExpenseReport(selectedReport.partitionKey, selectedReport.rowKey)
      .then((res) => setFullReport(res))
      .catch((err) => toast.error(`Failed to load receipts/line items: ${err.message}`))
      .finally(() => setLoadingFull(false));
  }, [selectedReport]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search) return reports;
    const s = search.toLowerCase();
    return reports.filter((r) =>
      r.claimRef?.toLowerCase().includes(s) ||
      r.claimPurpose?.toLowerCase().includes(s) ||
      r.requesterName?.toLowerCase().includes(s) ||
      r.requesterEmail?.toLowerCase().includes(s)
    );
  }, [reports, search]);

  // Single-report export → ZIP with XLSX + receipts/ folder.
  const handleExportBundle = async (report) => {
    setExportingId(report.rowKey);
    const tid = toast.loading('Preparing export — building XLSX + receipts…');
    try {
      const { blob, fileName } = await buildSingleReportBundle({
        userId: report.partitionKey,
        reportId: report.rowKey,
        displayName: report.requesterName,
        claimRef: report.claimRef,
      }, { onWarn: (m) => console.warn('[expense-bundle]', m) });
      downloadBlob(blob, fileName);
      toast.success('Export ready — downloading', { id: tid });
    } catch (err) {
      toast.error(`Export failed: ${err.message}`, { id: tid });
    } finally {
      setExportingId(null);
    }
  };

  const handleBulkExport = async () => {
    const toExport = filtered.filter((r) => r.rowKey);
    if (toExport.length === 0) {
      toast.error('No reports to export');
      return;
    }

    setExporting(true);
    const tid = toast.loading(`Preparing export… 0 of ${toExport.length}`);
    try {
      const { blob, fileName, included } = await buildBulkBundle(toExport, {
        onWarn: (m) => console.warn('[expense-bulk]', m),
        onProgress: ({ current, total }) => toast.loading(`Exporting ${current} of ${total}…`, { id: tid }),
      });
      if (!blob) {
        toast.error('No reports could be exported. Make sure the export template is configured.', { id: tid });
        return;
      }
      downloadBlob(blob, fileName);
      toast.success(`Exported ${included} report${included !== 1 ? 's' : ''} (XLSX + receipts) as ZIP`, { id: tid });
    } catch (err) {
      toast.error(err.message, { id: tid });
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: 'claimRef',
      label: 'Ref',
      render: (r) => <span className="font-mono text-xs font-semibold text-profun-cyan">{r.claimRef}</span>,
    },
    {
      key: 'requesterName',
      label: 'Employee',
      render: (r) => (
        <div>
          <div className="font-medium">{r.requesterName}</div>
          <div className="text-xs text-muted-foreground">{r.requesterDepartment}</div>
        </div>
      ),
    },
    {
      key: 'claimPurpose',
      label: 'Purpose',
      render: (r) => <span className="truncate max-w-[200px] block">{r.claimPurpose || '—'}</span>,
    },
    {
      key: 'totalClaimedSAR',
      label: 'Total (SAR)',
      render: (r) => <span className="font-mono font-semibold">{(r.totalClaimedSAR || 0).toFixed(2)}</span>,
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      render: (r) => {
        if (!r.submittedAt) return '—';
        return new Date(r.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
    {
      key: 'export',
      label: '',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); handleMarkPaid(r); }}
            disabled={markingPaidId === r.rowKey}
            className="p-1.5 rounded-lg text-profun-blue hover:bg-profun-blue/10 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            title="Mark as paid"
          >
            {markingPaidId === r.rowKey
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <BadgeCheck className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleExportBundle(r); }}
            disabled={exportingId === r.rowKey}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:pointer-events-none"
            title="Export bundle (XLSX + receipts)"
          >
            {exportingId === r.rowKey
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <FileSpreadsheet className="h-3.5 w-3.5" />}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Expense Reports</h1>
          <p className="text-muted-foreground">
            {reports.length} approved expense report{reports.length === 1 ? '' : 's'} ready to export
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setShowTemplateConfig(!showTemplateConfig)}>
            <Settings2 className="h-4 w-4" />
            Export Template
          </Button>
          <Button variant="secondary" onClick={handleBulkExport} disabled={exporting}>
            <Archive className="h-4 w-4" />
            {exporting ? 'Exporting...' : 'Export All'}
          </Button>
        </div>
      </div>

      {showTemplateConfig && (
        <div className="mb-6 border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between bg-muted/30 px-4 py-2 border-b border-border">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Export Template Configuration</span>
            <button onClick={() => setShowTemplateConfig(false)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Close</button>
          </div>
          <div className="p-4">
            <ExpenseTemplatePage />
          </div>
        </div>
      )}

      <div className="mb-4">
        <Input
          placeholder="Search by name, ref, or purpose..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-[400px]"
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage="No approved expense reports yet. They appear here once an approver signs off."
        keyFn={(r) => r.rowKey || r.claimRef}
        onRowClick={(r) => setSelectedReport(r)}
      />

      {/* Read-only quick-view modal — admin override edits live on the
          Expense Requests page, not here. Two-pane layout mirrors the
          Requests modal so admins have one mental model. */}
      {selectedReport && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedReport(null)}
          title={`Expense Report ${selectedReport.claimRef || ''}`}
          width="min(1600px, 96vw)"
        >
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-5">
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Employee</div>
                  <div className="font-medium">{selectedReport.requesterName}</div>
                  <div className="text-xs text-muted-foreground">{selectedReport.requesterEmail}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Status</div>
                  <Badge variant="success">approved</Badge>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Purpose</div>
                  <div>{selectedReport.claimPurpose || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Department</div>
                  <div>{selectedReport.requesterDepartment || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Office Location</div>
                  <div>{selectedReport.officeLocation || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Submitted</div>
                  <div>{selectedReport.submittedAt ? new Date(selectedReport.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</div>
                </div>
              </div>

              <div className="border-t border-border" />

              <div>
                <h4 className="text-sm font-semibold mb-3">Line Items</h4>
                {loadingFull ? (
                  <p className="text-sm text-muted-foreground">Loading line items…</p>
                ) : fullReport?.lineItems?.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/50">
                          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground">Date</th>
                          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground">Type</th>
                          <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-muted-foreground">Description</th>
                          <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-muted-foreground">Amount</th>
                          <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-muted-foreground">SAR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fullReport.lineItems.map((item, i) => (
                          <tr key={i} className="border-b border-border/50 last:border-0">
                            <td className="px-3 py-2">{item.date || item.expenseDate || '—'}</td>
                            <td className="px-3 py-2">{item.expenseTypeId || item.expenseType || '—'}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate">{item.description || '—'}</td>
                            <td className="px-3 py-2 text-right font-mono">{item.currencyType || ''} {parseFloat(item.currencyAmount || item.amount || 0).toFixed(2)}</td>
                            <td className="px-3 py-2 text-right">
                              <div className="font-mono font-semibold text-profun-cyan">{parseFloat(item.sarAmount || item.amount || 0).toFixed(2)}</div>
                              {fxNote(item) && (
                                <div className="mt-0.5 inline-flex items-center rounded-full bg-profun-cyan/10 px-1.5 py-0.5 text-[10px] font-medium text-profun-cyan whitespace-nowrap">{fxNote(item)}</div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No line items on this report.</p>
                )}
              </div>

              <div className="bg-muted/50 rounded-lg p-4 flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">Total Claimed (SAR)</span>
                <span className="text-lg font-bold font-mono text-profun-cyan">
                  {(selectedReport.totalClaimedSAR || 0).toFixed(2)}
                </span>
              </div>

              <div className="border-t border-border pt-3 flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => handleMarkPaid(selectedReport)}
                  loading={markingPaidId === selectedReport.rowKey}
                  className="border-transparent bg-profun-blue text-white hover:bg-profun-blue/90"
                >
                  <BadgeCheck className="h-4 w-4" /> Mark as paid
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleExportBundle(selectedReport)}
                  loading={exportingId === selectedReport.rowKey}
                >
                  <Download className="h-4 w-4" /> Export bundle (XLSX + receipts)
                </Button>
              </div>
            </div>

            <div className="lg:sticky lg:top-0 lg:self-start">
              <h4 className="text-sm font-semibold mb-3">
                Receipts {fullReport?.receipts ? `(${fullReport.receipts.length})` : ''}
              </h4>
              {loadingFull ? (
                <div className="flex h-[400px] items-center justify-center rounded-lg border border-dashed border-border bg-background/40 text-sm text-muted-foreground">
                  Loading receipts…
                </div>
              ) : (
                <ReceiptViewer
                  receipts={fullReport?.receipts || []}
                  emptyMessage="No receipts attached to this report."
                />
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
